import { Component, EventEmitter, Output } from '@angular/core';
import { MessageService } from 'primeng/api';
import { UniversalNoteService } from './universal-note.service';

interface EntityOption {
    id: number;
    label: string;
    sublabel: string;
}

@Component({
    selector: 'app-universal-note',
    templateUrl: './universal-note.component.html',
    styleUrls: ['./universal-note.component.css'],
    providers: [MessageService]
})
export class UniversalNoteComponent {
    display = false;
    saving = false;

    typeOptions = [
        { label: 'Franchisee', value: 'franchisee' },
        { label: 'Location', value: 'location' },
        { label: 'Client', value: 'client' },
        { label: 'Collections', value: 'collections' }
    ];

    // Copied verbatim from Franchise-FE/src/app/constant.ts:59-68
    callResultsList = [
        { title: 'Note Only: No Contact:', value: 'Note Only: No Contact:' },
        { title: 'Call to Franchisee', value: 'Call to Franchisee' },
        { title: 'Franchisee Called me', value: 'Franchisee Called me' },
        { title: 'Email to Franchisee', value: 'Email to Franchisee' },
        { title: 'Franchisee Emailed me', value: 'Franchisee Emailed me' },
        { title: 'Texted with Franchisee,', value: 'Texted with Franchisee,' },
        { title: 'Office Meeting with Franchisee', value: 'Office Meeting with Franchisee' },
        { title: 'Field Meeting with Franchisee', value: 'Field Meeting with Franchisee' }
    ];

    // Names must match the backend validator exactly: 'Left Voice Mail for' | 'Sent Email to' | 'Spoke to'
    callOptions = [
        { name: 'Left Voice Mail for', value: '1' },
        { name: 'Sent Email to', value: '2' },
        { name: 'Spoke to', value: '3' }
    ];

    selectedType: string | null = null;
    selectedEntity: EntityOption | null = null;
    entityOptions: EntityOption[] = [];
    loadingEntities = false;
    note = '';
    callResult: string | null = null;
    callOption: any = null;
    contactDate: Date = new Date();

    @Output() saved = new EventEmitter<void>();

    constructor(
        private universalNoteService: UniversalNoteService,
        private messageService: MessageService
    ) { }

    open() {
        this.resetForm();
        this.display = true;
    }

    onTypeChange() {
        this.selectedEntity = null;
        this.entityOptions = [];
        this.note = '';
        this.callResult = null;
        this.callOption = null;
        this.contactDate = new Date();
        this.loadEntities();
    }

    private searchSeq = 0;

    private loadEntities() {
        if (!this.selectedType) {
            return;
        }
        // Sequence guard: a slow earlier response must not overwrite a newer type's results
        const seq = ++this.searchSeq;
        this.loadingEntities = true;
        this.universalNoteService.searchEntities(this.selectedType, '').subscribe({
            next: (response) => {
                if (seq !== this.searchSeq) { return; }
                this.loadingEntities = false;
                this.entityOptions = response.data || [];
            },
            error: () => {
                if (seq !== this.searchSeq) { return; }
                this.loadingEntities = false;
                this.entityOptions = [];
            }
        });
    }

    get recordLink(): string | null {
        if (!this.selectedType || !this.selectedEntity?.id) {
            return null;
        }
        switch (this.selectedType) {
            case 'franchisee': return '/franchise/edit/' + this.selectedEntity.id;
            case 'client': return '/client/edit/' + this.selectedEntity.id;
            case 'location': return '/client/servicelocations/edit/' + this.selectedEntity.id;
            // Entity id is Clients.fId, which is what the statement detail route expects.
            case 'collections': return '/account/collectionstatementdetail/' + this.selectedEntity.id;
            default: return null;
        }
    }

    openRecord() {
        if (this.recordLink) {
            window.open(this.recordLink, '_blank');
        }
    }

    get canSave(): boolean {
        if (!this.selectedType || !this.selectedEntity?.id || !this.note.trim()) {
            return false;
        }
        if (this.selectedType === 'franchisee' && !this.callResult) {
            return false;
        }
        if (this.selectedType === 'collections' && (!this.callOption || !this.contactDate)) {
            return false;
        }
        return true;
    }

    save() {
        if (!this.canSave || this.saving) {
            return;
        }
        const payload: any = {
            type: this.selectedType,
            entityId: this.selectedEntity!.id,
            note: this.note.trim()
        };
        if (this.selectedType === 'franchisee') {
            payload.callresult = this.callResult;
        }
        if (this.selectedType === 'collections') {
            payload.calloption = this.callOption.name;
            payload.contactdate = this.formatDate(this.contactDate);
        }

        this.saving = true;
        this.universalNoteService.addNote(payload).subscribe({
            next: (response) => {
                this.saving = false;
                if (response.success) {
                    this.messageService.add({ severity: 'success', summary: 'Note added', detail: response.message });
                    this.display = false;
                    this.saved.emit();
                } else {
                    this.messageService.add({ severity: 'error', summary: 'Could not save note', detail: response.message });
                }
            },
            error: () => {
                this.saving = false;
                this.messageService.add({ severity: 'error', summary: 'Could not save note', detail: 'Something went wrong. Please try again.' });
            }
        });
    }

    private formatDate(d: Date): string {
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return mm + '/' + dd + '/' + d.getFullYear();
    }

    private resetForm() {
        this.selectedType = null;
        this.onTypeChange();
    }
}
